/* eslint-disable zeffy-linter-frontend/no-classname-prop -- legacy baseline (pre-rule); remove when these are migrated */
import { ReactElement, useCallback, useState, Ref, useRef, useMemo, useContext, MouseEvent } from "react";

import { FilledTextFieldProps, OutlinedTextFieldProps, StandardTextFieldProps } from "@mui/material/TextField";
import { DateTimePicker as MaterialDateTimePicker } from "@mui/x-date-pickers-pro";
import { getLuxonFormatFromLocale, isAmPm } from "@simplyk/common";
import { DateTime } from "luxon";
import { FieldValues } from "react-hook-form";

import { useLocaleContext } from "../../../common/contexts/LocaleContext";
import { DatePickerLeftArrow, DatePickerRightArrow } from "../DashboardDatePicker/DatePickerArrows";
import { DashboardTextField } from "../TextField/DashboardTextField";

import { DateTimePickerDay } from "./DateTimePickerDay";
import { DateTimePickerTextFieldContext } from "./DateTimePickerTextFieldContext";
import { arrowIconButton, popover } from "./styles";
import { ControlledDateTimePickerProps } from "./types";

import { useCurrentInputVibe } from "@/common/hooks/useCurrentInputVibe";
import { useFormInputRefs } from "@/common/hooks/useFormInputRefs";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { Schedule as ScheduleIcon } from "@/icons/outlined";

export function ControlledDateTimePicker<T extends FieldValues>({
  inputRef,
  className,
  vibe,
  disabled,
  inputProps,
  name,
  size,
  error,
  errorMessage,
  endElement,
  helperText,
  label,
  onChange,
  ref,
  value,
  onBlur,
  minDate,
  maxDate,
  disablePast,
  "data-test": dataTest,
}: ControlledDateTimePickerProps<T>): ReactElement<ControlledDateTimePickerProps<T>> {
  const { isoLocale } = useLocaleContext();

  const [open, setOpen] = useState(false);
  const [isOpenedFromInput, setIsOpenedFromInput] = useState(false);

  const isClickingInputRef = useRef(false);
  const popperRef = useRef<HTMLInputElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  const onOpen = useCallback(() => {
    openerRef.current = getFocusedElement();
    setIsOpenedFromInput(false);
    setOpen(true);
  }, []);
  const onClose = useCallback(() => {
    if (isClickingInputRef.current) {
      return;
    }
    skipFocusRestoreWhenTypingElsewhere(openerRef.current);
    setOpen(false);
  }, []);

  const onOpenFromInput = useCallback(
    (input: HTMLInputElement) => {
      isClickingInputRef.current = true;
      setTimeout(() => {
        isClickingInputRef.current = false;
      });
      if (!open) {
        openerRef.current = input;
        setIsOpenedFromInput(true);
        setOpen(true);
      } else if (!isOpenedFromInput) {
        setIsOpenedFromInput(true);
        setTimeout(() => input.focus());
      }
    },
    [isOpenedFromInput, open]
  );

  const handleChangeDate = useCallback(
    (newDate: DateTime | null) => {
      onChange?.(newDate?.toJSDate() || null);
    },
    [onChange]
  );

  const refCallback = useFormInputRefs({ inputRefCallBack: ref, inputRef, inputRefs: [popperRef] });

  const arrowProps = useMemo(() => ({ sx: arrowIconButton, disableRipple: true, tabIndex: -1 }), []);

  return (
    <DateTimePickerTextFieldContext.Provider
      value={{
        "data-test": dataTest,
        error,
        errorMessage,
        name,
        onOpen,
        onOpenFromInput,
        isOpenedFromInput,
        refCallback,
        size,
        vibe,
        endElement,
        helperText,
      }}
    >
      <MaterialDateTimePicker
        ref={ref as Ref<HTMLInputElement>}
        slotProps={{
          nextIconButton: arrowProps,
          previousIconButton: arrowProps,
          popper: {
            sx: popover,
            anchorEl: popperRef.current,
          },
          desktopTrapFocus: { disableEnforceFocus: isOpenedFromInput },
          textField: { label, inputProps, className, onBlur },
        }}
        slots={{
          rightArrowIcon: DatePickerRightArrow,
          leftArrowIcon: DatePickerLeftArrow,
          textField: DateTimePickerField,
          day: DateTimePickerDay,
        }}
        ampm={isAmPm(isoLocale)}
        disabled={disabled}
        onChange={handleChangeDate}
        onClose={onClose}
        onOpen={onOpen}
        open={open}
        inputRef={refCallback}
        value={value ? DateTime.fromJSDate(value) : null}
        format={getLuxonFormatFromLocale(isoLocale)}
        minDate={minDate ? DateTime.fromJSDate(minDate) : undefined}
        maxDate={maxDate ? DateTime.fromJSDate(maxDate) : undefined}
        disablePast={disablePast}
      />
    </DateTimePickerTextFieldContext.Provider>
  );
}

const DateTimePickerField = ({
  className,
  disabled,
  inputProps,
  label,
  onBlur,
  onChange,
  onFocus,
  value,
  placeholder,
  onMouseUp,
  onClick,
}: StandardTextFieldProps | FilledTextFieldProps | OutlinedTextFieldProps) => {
  const {
    "data-test": dataTest,
    error,
    errorMessage,
    name,
    onOpen,
    onOpenFromInput,
    refCallback,
    size,
    vibe,
    endElement,
    helperText,
  } = useContext(DateTimePickerTextFieldContext);

  const currentInputVibe = useCurrentInputVibe(vibe);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      onClick?.(event);
      if (!disabled && event.target instanceof HTMLInputElement) {
        onOpenFromInput(event.target);
      }
    },
    [disabled, onClick, onOpenFromInput]
  );

  return (
    <DashboardTextField
      className={className}
      data-test={dataTest}
      errorMessage={errorMessage}
      error={error}
      inputRef={refCallback}
      vibe={currentInputVibe}
      disabled={disabled}
      endAdornment={
        <DashboardIconButton
          inline
          size={size === "small" ? "medium" : "large"}
          vibe="neutral"
          variant="text"
          disabled={disabled}
          onClick={!disabled ? onOpen : undefined}
          data-test={`${dataTest}-button`}
        >
          <ScheduleIcon />
        </DashboardIconButton>
      }
      inputProps={inputProps}
      name={name}
      onBlur={onBlur}
      onChange={onChange}
      onFocus={onFocus}
      size={size}
      value={value}
      label={label}
      helperText={helperText}
      endElement={endElement}
      placeholder={placeholder}
      onMouseUp={onMouseUp}
      onClick={handleClick}
    />
  );
};

const FOCUS_RESTORE_GUARD_MS = 100;

const skipFocusRestoreWhenTypingElsewhere = (opener: HTMLElement | null) => {
  const clickedElement = document.activeElement;
  const isTypingElsewhere =
    (clickedElement instanceof HTMLInputElement ||
      clickedElement instanceof HTMLTextAreaElement ||
      (clickedElement instanceof HTMLElement && clickedElement.isContentEditable)) &&
    clickedElement !== opener;
  if (!opener || !isTypingElsewhere) {
    return;
  }

  opener.focus = () => {};
  setTimeout(() => Reflect.deleteProperty(opener, "focus"), FOCUS_RESTORE_GUARD_MS);
};

const getFocusedElement = () => (document.activeElement instanceof HTMLElement ? document.activeElement : null);
